import type { Probe, ProbeResult } from '../engine/types';
import { ok, na } from '../engine/runner';
import { registerProbe } from '../engine/registry';

/**
 * WebSocket handshake metadata.
 *
 * The reference site opens a WebSocket and reports the negotiated URL and
 * subprotocol. We do the same against our own origin — no third-party service,
 * nothing transmitted. The useful part is which subprotocols the browser
 * accepts and what the server negotiates back; a headless client that rewrites
 * WebSocket globals can diverge here.
 *
 * The handshake only runs when VITE_WS_ENDPOINT is configured; without it the
 * probe returns no signals (a bare "WebSocket is supported" boolean is true in
 * every browser and was constant noise).
 *
 * The connection is closed immediately and carries no application data.
 */
export const websocketProbe: Probe = {
  id: 'websocket',
  label: 'WebSocket',
  layer: 'network',
  tier: 'client',
  category: 'network',
  description: 'WebSocket handshake and subprotocol negotiation (local echo)',
  budgetMs: 1500,

  async run(): Promise<ProbeResult> {
    if (typeof WebSocket === 'undefined') {
      return { signals: { supported: na<boolean>('unsupported', 'client', 'WebSocket absent') } };
    }

    const url = buildLocalUrl();
    if (!url) {
      return {
        signals: { supported: ok(false, 'client'), note: ok('page not served over http(s)', 'client') },
      };
    }

    // Attempting a handshake against our own origin is EXPECTED to fail on a
    // static host (there is no /ws endpoint), and every attempt logs a console
    // warning ("WebSocket connection ... failed") that has nothing to do with the
    // visitor. We only attempt it when a real WebSocket endpoint is configured.
    // Without one the probe returns NOTHING: "WebSocket exists" is true in every
    // browser that has ever shipped fetch, so reporting it was constant noise.
    const wsEndpoint = (import.meta.env?.VITE_WS_ENDPOINT as string | undefined) ?? null;
    if (!wsEndpoint) {
      return { signals: {} };
    }

    const attempted = { url, opened: false, protocol: '', error: null as string | null };

    try {
      await new Promise<void>((resolve) => {
        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          try {
            ws.close();
          } catch {
            /* ignore */
          }
          resolve();
        };
        let ws: WebSocket;
        try {
          ws = new WebSocket(url);
        } catch (e) {
          attempted.error = String((e as Error)?.message ?? e);
          finish();
          return;
        }
        // A static host has no WebSocket endpoint, so the handshake is
        // expected to fail fast. Keep the wait well under the probe budget so
        // the result is always reported rather than timing out silently.
        const timer = setTimeout(finish, 900);
        ws.onopen = () => {
          attempted.opened = true;
          attempted.protocol = ws.protocol;
          clearTimeout(timer);
          finish();
        };
        ws.onerror = () => {
          attempted.error = 'handshake failed (expected on a static host with no WS endpoint)';
          clearTimeout(timer);
          finish();
        };
        ws.onclose = () => {
          clearTimeout(timer);
          finish();
        };
      });
    } catch (e) {
      attempted.error = String((e as Error)?.message ?? e);
    }

    return {
      signals: {
        supported: ok(true, 'client'),
        // Probe our own origin over ws(s) to avoid a third-party dependency.
        attemptedUrl: ok(attempted.url, 'client'),
        handshakeOpened: ok(attempted.opened, 'client'),
        negotiatedProtocol: ok(attempted.protocol || null, 'client'),
        handshakeError: ok(attempted.error, 'client'),
        binaryTypeSupported: ok(typeof WebSocket.prototype.binaryType === 'string', 'client'),
      },
    };
  },
};

function buildLocalUrl(): string | null {
  if (typeof location === 'undefined') return null;
  const { protocol, host } = location;
  if (protocol === 'https:') return `wss://${host}/`;
  if (protocol === 'http:') return `ws://${host}/`;
  return null;
}

registerProbe(websocketProbe);
